import { AlertTriangle } from 'lucide-react'
import { useAcknowledgeOverage, useBillingUsage } from '@/api/hooks/use-billing'

/**
 * Shows a warning banner when the account has unacknowledged overage strikes.
 * The user must acknowledge they understand overage billing before the banner dismisses.
 */
export function OverageBanner(): React.JSX.Element | null {
  const { data } = useBillingUsage()
  const acknowledgeMutation = useAcknowledgeOverage()

  if (!data) return null

  const { strikes, acknowledged } = data.overage_policy
  if (strikes <= 0 || acknowledged) return null

  const overage = data.emails.overage

  return (
    <div className="rounded-xl border border-warning/40 bg-warning/5 p-5">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
        <div className="flex-1">
          <h3 className="font-medium text-text-primary">Overage Notice</h3>
          <p className="mt-1 text-sm text-text-secondary">
            You have exceeded your plan&apos;s email limit by{' '}
            <span className="font-semibold">{overage.toLocaleString()}</span> emails this period.
            {strikes >= 3
              ? ' Your account has been flagged for repeated overages. Please upgrade your plan to avoid sending interruptions.'
              : ` You have ${strikes} overage strike${strikes !== 1 ? 's' : ''}. At 3 strikes, sending may be paused.`}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => acknowledgeMutation.mutate()}
              disabled={acknowledgeMutation.isPending}
              className="rounded-lg bg-warning px-4 py-2 text-sm font-medium text-white hover:bg-warning/90 disabled:opacity-50"
            >
              {acknowledgeMutation.isPending ? 'Acknowledging...' : 'I understand'}
            </button>
            <a
              href="/choose-plan?category=transactional"
              className="text-sm font-medium text-brand-500 hover:text-brand-600"
            >
              Upgrade plan
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
